import { Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import React from "react";
import { brandColors, designTokens, ModernButton } from "../Modern";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";

const AlertDialog = ({
  title = null,
  message = null,
  importantMsg = null,
  handleConfirm,
  handleDeny,
  confirmText = "Sim",
  denyText = "Não",
  oneButton = false,
}) => (
  <>
    <DialogContent sx={styles.content}>
      {title && <Typography sx={styles.content.title}>{title}</Typography>}
      {message && <Typography sx={styles.content.message}>{message}</Typography>}
      {importantMsg && <Typography sx={styles.content.importantMsg}>{importantMsg}</Typography>}
    </DialogContent>
    <DialogActions sx={styles.actions}>
      {!oneButton && (
        <ModernButton
          color="secondary"
          variant="outlined"
          size="small"
          autoFocus
          onClick={handleDeny}
        >
          {denyText}
        </ModernButton>
      )}
      <ModernButton
        variant="outlined"
        size="small"
        sx={{ px: designTokens.spacing.xs }}
        onClick={handleConfirm}
      >
        {confirmText}
      </ModernButton>
    </DialogActions>
  </>
);

const DialogBox = ({ config, onClose }) => {
  const {
    customComponent: CustomComponent = false,
    open = false,
    callback = () => {},
    negativeCallback = () => {},
    message,
    confirmText,
    denyText,
    fullScreen,
    blockScreen = false, //block screen é para não deixar sair do modal até responder, como no Re-login quando o token expira
    maxWidth = false, // 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
    fullWidth = false,
    title,
    oneButton,
    importantMsg,
  } = config;

  const handleConfirm = () => {
    callback();
    onClose();
  };

  const handleDeny = () => {
    negativeCallback();
    onClose();
  };

  const render = () => {
    if (CustomComponent) {
      // O DialogProvider fica fora do ErrorBoundary das rotas: sem esta proteção, um erro de
      // renderização dentro do dialog desmonta a árvore inteira (tela branca).
      return (
        <ErrorBoundary
          fallback={
            <DialogContent sx={styles.content}>
              <Typography sx={styles.content.title}>Algo deu errado</Typography>
              <Typography sx={styles.content.message}>
                Não foi possível exibir esta janela. A operação pode ter sido concluída; atualize a
                tela para conferir.
              </Typography>
              <DialogActions sx={styles.actions}>
                <ModernButton variant="outlined" size="small" onClick={handleDeny}>
                  Fechar
                </ModernButton>
              </DialogActions>
            </DialogContent>
          }
        >
          <CustomComponent handleDeny={handleDeny} />
        </ErrorBoundary>
      );
    }
    if (message) {
      return (
        <AlertDialog
          handleConfirm={handleConfirm}
          handleDeny={handleDeny}
          confirmText={confirmText}
          denyText={denyText}
          message={message}
          title={title}
          oneButton={oneButton}
          importantMsg={importantMsg}
        />
      );
    }
    return null;
  };

  return (
    <Dialog
      sx={styles.container}
      open={open}
      onClose={blockScreen ? null : handleDeny}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      {render()}
    </Dialog>
  );
};

export default DialogBox;

const text = {
  color: brandColors.neutral[800],
  fontSize: "1rem",
  fontFamily: "Montserrat",
};

const styles = {
  container: {
    "& .MuiDialog-paper": {
      borderRadius: "12px",
    },
  },
  content: {
    textAlign: "left",
    title: {
      ...text,
      fontWeight: "700",
      fontSize: "1.35rem",
      mb: 1,
    },
    message: {
      ...text,
    },
    importantMsg: {
      ...text,
      fontWeight: "600",
    },
  },
  actions: {
    justifyContent: "end",
    mb: 2,
    mr: 2,
  },
  button: {
    fontFamily: "Montserrat",
    px: designTokens.spacing.xs,
  },
};
